import React, { useState, useRef, useEffect } from 'react';
import { X, Loader2, Image, Mic, Upload, Rocket, CheckCircle, RefreshCw, Video, ChevronLeft, ChevronRight, Save, FileText } from 'lucide-react';
import { genCoverPrompt, genCoverImg, uploadAudio, uploadVideo, getVersionMedia, saveAudioMsg, updateFile } from '../../../../lib/apis_vibe';

interface PublishPrepareModalProps {
  isOpen: boolean;
  onClose: () => void;
  versionId: number | null;
  fileId: number | null;
  onPublish: (fileName?: string) => Promise<void>;
}

const PublishPrepareModal: React.FC<PublishPrepareModalProps> = ({ 
  isOpen, 
  onClose, 
  versionId,
  fileId,
  onPublish 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  
  const [fileName, setFileName] = useState<string>('');
  const [coverPrompt, setCoverPrompt] = useState<string>('');
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioMsg, setAudioMsg] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  
  const [isSavingFileName, setIsSavingFileName] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isGeneratingImg, setIsGeneratingImg] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [isSavingAudioMsg, setIsSavingAudioMsg] = useState(false);
  
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoRecordingTime, setVideoRecordingTime] = useState(0);
  const [hasExistingCover, setHasExistingCover] = useState(false);
  const [hasExistingAudio, setHasExistingAudio] = useState(false);
  const [hasExistingVideo, setHasExistingVideo] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const videoTimerRef = useRef<NodeJS.Timeout | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen && versionId) {
      setCurrentStep(1);
      loadExistingMedia();
    }
  }, [isOpen, versionId]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (videoTimerRef.current) {
        clearInterval(videoTimerRef.current);
      }
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const loadExistingMedia = async () => {
    if (!versionId) return;
    try {
      const res = await getVersionMedia(versionId);
      const data = (res as any).data || res;
      
      if (data.file_name) {
        setFileName(data.file_name);
      }
      if (data.img_url) {
        setCoverImageUrl(data.img_url);
        setHasExistingCover(true);
      }
      if (data.audio_url) {
        setAudioUrl(data.audio_url);
        setHasExistingAudio(true);
      }
      if (data.audio_msg) {
        setAudioMsg(data.audio_msg);
      }
      if (data.video_url) {
        setVideoUrl(data.video_url);
        setHasExistingVideo(true);
      }
    } catch (e) {
      console.error('Failed to load existing media:', e);
    }
  };

  const handleSaveFileName = async () => {
    if (!fileId || !fileName.trim()) return;
    setIsSavingFileName(true);
    try {
      await updateFile(fileId, { name: fileName.trim() });
      alert('作品名称保存成功！');
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '保存失败';
      alert(errorMsg);
    } finally {
      setIsSavingFileName(false);
    }
  };

  const handleGeneratePrompt = async () => {
    if (!versionId) return;
    setIsGeneratingPrompt(true);
    try {
      const res = await genCoverPrompt(versionId);
      const data = (res as any).data || res;
      setCoverPrompt(data.prompt);
      setIsEditingPrompt(true);
      
      if (data.from_cache && coverImageUrl) {
        setHasExistingCover(true);
      }
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '生成提示词失败';
      alert(errorMsg);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!versionId || !coverPrompt.trim()) {
      alert('请先生成封面提示词');
      return;
    }
    setIsGeneratingImg(true);
    try {
      const res = await genCoverImg(versionId, coverPrompt);
      const data = (res as any).data || res;
      setCoverImageUrl(data.image_url);
      setHasExistingCover(true);
      setIsEditingPrompt(false);
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '生成封面图失败';
      alert(errorMsg);
    } finally {
      setIsGeneratingImg(false);
    }
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        handleUploadAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      setAudioMsg('');
      
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'zh-CN';
        
        recognitionRef.current.onresult = (event: any) => {
          let finalTranscript = '';
          let interimTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          
          if (finalTranscript) {
            setAudioMsg(prev => prev + finalTranscript);
          }
        };
        
        recognitionRef.current.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
        };
        
        recognitionRef.current.start();
      }
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 8) {
            handleStopRecording();
            return 8;
          }
          return prev + 1;
        });
      }, 1000);
      
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          handleStopRecording();
        }
      }, 8000);
      
    } catch (e) {
      alert('无法访问麦克风，请检查权限设置');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleUploadAudio = async (audioBlob: Blob) => {
    if (!versionId) return;
    setIsUploadingAudio(true);
    try {
      const file = new File([audioBlob], `recording_${Date.now()}.webm`, { type: 'audio/webm' });
      const res = await uploadAudio(versionId, file, audioMsg);
      const data = (res as any).data || res;
      setAudioUrl(data.audio_url);
      setHasExistingAudio(true);
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '上传录音失败';
      alert(errorMsg);
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const handleSaveAudioMsg = async () => {
    if (!versionId || !audioMsg.trim()) return;
    setIsSavingAudioMsg(true);
    try {
      await saveAudioMsg(versionId, audioMsg);
      alert('保存成功！');
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '保存失败';
      alert(errorMsg);
    } finally {
      setIsSavingAudioMsg(false);
    }
  };

  const handleStartVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: true 
      });
      
      videoStreamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        handleUploadVideo(videoBlob);
      };
      
      setIsRecordingVideo(true);
      setVideoRecordingTime(0);
      
      setTimeout(() => {
        if (videoPreviewRef.current && videoStreamRef.current) {
          videoPreviewRef.current.srcObject = videoStreamRef.current;
          videoPreviewRef.current.play().catch(e => console.error('Video play error:', e));
        }
      }, 100);
      
      mediaRecorder.start();
      
      videoTimerRef.current = setInterval(() => {
        setVideoRecordingTime(prev => {
          if (prev >= 10) {
            handleStopVideoRecording();
            return 10;
          }
          return prev + 1;
        });
      }, 1000);
      
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          handleStopVideoRecording();
        }
      }, 10000);
      
    } catch (e) {
      alert('无法访问摄像头，请检查权限设置');
    }
  };

  const handleStopVideoRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecordingVideo(false);
      if (videoTimerRef.current) {
        clearInterval(videoTimerRef.current);
      }
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
      }
    }
  };

  const handleUploadVideo = async (videoBlob: Blob) => {
    if (!versionId) return;
    setIsUploadingVideo(true);
    try {
      const file = new File([videoBlob], `video_${Date.now()}.webm`, { type: 'video/webm' });
      const res = await uploadVideo(versionId, file);
      const data = (res as any).data || res;
      setVideoUrl(data.video_url);
      setHasExistingVideo(true);
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || e.message || '上传视频失败';
      alert(errorMsg);
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handlePublish = async () => {
    if (!coverImageUrl) {
      alert('请先生成封面图');
      return;
    }
    
    setIsPublishing(true);
    try {
      await onPublish(fileName.trim() || undefined);
      onClose();
    } catch (e) {
      console.error('Publish failed:', e);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleNextStep = () => {
    if (!coverImageUrl) {
      alert('请先生成封面图');
      return;
    }
    setCurrentStep(2);
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[200] animate-in fade-in duration-300">
      <div className="bg-[#18181b] border border-white/10 w-full max-w-2xl rounded-2xl shadow-2xl p-8 relative max-h-[90vh] overflow-y-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">发布准备</h2>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-purple-600' : 'bg-white/10'}`}>
              <span className="text-white text-sm font-bold">1</span>
            </div>
            <div className="w-8 h-0.5 bg-white/20"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-purple-600' : 'bg-white/10'}`}>
              <span className="text-white text-sm font-bold">2</span>
            </div>
          </div>
        </div>
        
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500/20">
                    <FileText size={16} className="text-purple-400" />
                  </div>
                  <h3 className="text-white font-medium">作品名称</h3>
                </div>
                <button
                  onClick={handleSaveFileName}
                  disabled={isSavingFileName || !fileName.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-green-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
                >
                  {isSavingFileName ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      保存中...
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      保存
                    </>
                  )}
                </button>
              </div>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="请输入作品名称..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-500"
              />
            </div>

            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${coverImageUrl ? 'bg-green-500/20' : 'bg-white/5'}`}>
                    {coverImageUrl ? <CheckCircle size={16} className="text-green-500" /> : <span className="text-gray-400 text-sm font-bold">1</span>}
                  </div>
                  <h3 className="text-white font-medium">封面生成</h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleGeneratePrompt}
                    disabled={isGeneratingPrompt}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    {isGeneratingPrompt ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        生成中...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} />
                        生成提示词
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImg || !coverPrompt}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    {isGeneratingImg ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        生成中...
                      </>
                    ) : (
                      <>
                        <Image size={14} />
                        生成图片
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="mt-3">
                {coverImageUrl && !isEditingPrompt ? (
                  <div className="flex justify-center">
                    <img 
                      src={`${coverImageUrl}?t=${Date.now()}`} 
                      alt="封面图" 
                      className="max-h-40 rounded-lg border border-white/10"
                    />
                  </div>
                ) : coverPrompt ? (
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-gray-300 text-sm">{coverPrompt}</p>
                  </div>
                ) : (
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <p className="text-gray-500 text-sm">请先生成封面提示词</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${audioUrl ? 'bg-green-500/20' : 'bg-white/5'}`}>
                    {audioUrl ? <CheckCircle size={16} className="text-green-500" /> : <span className="text-gray-400 text-sm font-bold">2</span>}
                  </div>
                  <h3 className="text-white font-medium">录制灵感音频（可选）</h3>
                </div>
                <button
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={isUploadingAudio}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
                    isRecording 
                      ? 'bg-red-600 hover:bg-red-500' 
                      : 'bg-green-600 hover:bg-green-500'
                  }`}
                >
                  {isUploadingAudio ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      上传中...
                    </>
                  ) : isRecording ? (
                    <>
                      <Mic size={14} className="animate-pulse" />
                      停止 ({recordingTime}s/8s)
                    </>
                  ) : (
                    <>
                      <Mic size={14} />
                      {audioUrl ? '重新录音' : '开始录音'}
                    </>
                  )}
                </button>
              </div>
              {audioUrl && (
                <div className="mt-3 flex justify-center">
                  <audio controls src={audioUrl} className="w-full max-w-md">
                    您的浏览器不支持音频播放
                  </audio>
                </div>
              )}
              {isRecording && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-400 text-sm">正在录音... {recordingTime}s / 8s</span>
                </div>
              )}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">录音文本</span>
                  {audioMsg && (
                    <button
                      onClick={handleSaveAudioMsg}
                      disabled={isSavingAudioMsg}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white rounded text-xs transition-colors"
                    >
                      {isSavingAudioMsg ? (
                        <>
                          <Loader2 size={10} className="animate-spin" />
                          保存中...
                        </>
                      ) : (
                        <>
                          <Save size={10} />
                          保存
                        </>
                      )}
                    </button>
                  )}
                </div>
                <textarea
                  value={audioMsg}
                  onChange={(e) => setAudioMsg(e.target.value)}
                  placeholder="录音时会自动转文字，也可以手动编辑..."
                  className="w-full h-20 bg-white/5 border border-white/10 rounded-lg p-2 text-gray-300 text-sm resize-none focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${videoUrl ? 'bg-green-500/20' : 'bg-white/5'}`}>
                    {videoUrl ? <CheckCircle size={16} className="text-green-500" /> : <span className="text-gray-400 text-sm font-bold">3</span>}
                  </div>
                  <h3 className="text-white font-medium">录制宣传视频（可选）</h3>
                </div>
                <button
                  onClick={isRecordingVideo ? handleStopVideoRecording : handleStartVideoRecording}
                  disabled={isUploadingVideo}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
                    isRecordingVideo 
                      ? 'bg-red-600 hover:bg-red-500' 
                      : 'bg-orange-600 hover:bg-orange-500'
                  }`}
                >
                  {isUploadingVideo ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      上传中...
                    </>
                  ) : isRecordingVideo ? (
                    <>
                      <Video size={14} className="animate-pulse" />
                      停止 ({videoRecordingTime}s/10s)
                    </>
                  ) : (
                    <>
                      <Video size={14} />
                      {videoUrl ? '重新录制' : '开始录制'}
                    </>
                  )}
                </button>
              </div>
              
              <div className={`mt-3 ${isRecordingVideo ? '' : 'hidden'}`}>
                <video 
                  ref={videoPreviewRef}
                  className="w-full max-w-md mx-auto h-48 rounded-lg bg-black"
                  autoPlay
                  muted
                  playsInline
                />
                {isRecordingVideo && (
                  <div className="mt-2 flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-400 text-sm">正在录制... {videoRecordingTime}s / 10s</span>
                  </div>
                )}
              </div>
              
              {videoUrl && !isRecordingVideo && (
                <div className="mt-3 flex justify-center">
                  <video controls src={videoUrl} className="w-full max-w-md h-48 rounded-lg">
                    您的浏览器不支持视频播放
                  </video>
                </div>
              )}
              
              {!videoUrl && !isRecordingVideo && (
                <div className="mt-3 bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-gray-500 text-sm">点击"开始录制"使用摄像头录制10秒宣传视频</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-3">
          {currentStep === 1 ? (
            <>
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-xl font-medium text-sm transition-all"
              >
                取消
              </button>
              <button
                onClick={handleNextStep}
                disabled={!coverImageUrl}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-all"
              >
                下一步
                <ChevronRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handlePrevStep}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-xl font-medium text-sm transition-all"
              >
                <ChevronLeft size={16} />
                上一步
              </button>
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:from-purple-600/50 disabled:to-blue-600/50 text-white rounded-xl font-bold text-sm transition-all shadow-lg"
              >
                {isPublishing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    发布中...
                  </>
                ) : (
                  <>
                    <Rocket size={16} />
                    发布
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishPrepareModal;
